// Discount code management utilities
// Centralized location for managing all discount codes and their configurations

// Base subscription price in cents (Stripe format)
export const BASE_SUBSCRIPTION_PRICE = 250000; // $2,500 in cents 

// Discount code configurations
export const DISCOUNT_CODES = {
  ECRIO: {
    code: 'ECRIO',
    discountPercentage: 20, // 20% off
    description: '20% off subscription',
    validFrom: new Date('2025-09-23'), // September 23, 2025
    validUntil: new Date('2025-10-31'), // October 31, 2025
    isActive: true,
    maxUses: null, // No limit
    currentUses: 0, // Track usage if needed
  },
  FrenchFlorist: {
    code: 'FrenchFlorist',
    discountPercentage: 40,
    description: '40% off subscription',
    validFrom: new Date('2025-12-29'),
    validUntil: new Date('2026-01-31'),
    isActive: true,
    maxUses: null,
    currentUses: 0,
    allowedTenants: ['redwoodseed'], // Only for Redwood
  },
  redwoodadmin: {
    code: 'redwoodadmin',
    discountPercentage: 99, // 99% off
    description: '99% off subscription for Redwood admin',
    validFrom: new Date('2026-02-24'), // adjust if needed
    validUntil: new Date('2026-12-31'), // adjust if needed
    isActive: true,
    maxUses: null,
    currentUses: 0,
    allowedTenants: ['redwoodseed'], // only for Redwood
    finalPriceOverrideCents: 100,
  },
  Summer26: {
    code: 'Summer26',
    discountPercentage: 20, // 20% off
    description: '20% off subscription for Summer 2026',
    validFrom: new Date('2026-06-23'), // adjust if needed
    validUntil: new Date('2026-09-02'), // adjust if needed
    isActive: true,
    maxUses: null,
    currentUses: 0,
    allowedTenants: ['redwoodseed'], 
    // finalPriceOverrideCents: 100,
  },
  Bridge409A:{
    code: 'Bridge409A',
    discountPercentage: 50, // 50% off
    description: '50% off subscription for Bridge 409A',
    validFrom: new Date('2026-08-27'), // adjust if needed
    validUntil: new Date('2026-09-30T23:59:59'), // adjust if needed
    isActive: true,
    maxUses: null,
    currentUses: 0,
    allowedTenants: ['redwoodseed'], 
  }
  // Add more discount codes here as needed
  // EXAMPLE_CODE: {
  //   code: 'EXAMPLE_CODE',
  //   discountPercentage: 10,
  //   description: '10% off subscription',
  //   validFrom: new Date('2024-01-01'),
  //   validUntil: new Date('2024-12-31'),
  //   isActive: true,
  //   maxUses: 100,
  //   currentUses: 0,
  // },
};

/**
 * Validates a discount code and returns discount information
 * @param {string} code - The discount code to validate
 * @param {string} currentTenant - Optional current tenant to validate against
 * @returns {Object} - Validation result with discount details
 */
export function validateDiscountCode(code, currentTenant) {
  if (!code || typeof code !== 'string') {
    return {
      isValid: false,
      message: 'Please enter a valid discount code',
      discountData: null,
    };
  }

  const normalizedCode = code.trim();
  const discountConfig = DISCOUNT_CODES[normalizedCode];

  if (!discountConfig) {
    return {
      isValid: false,
      message: 'Invalid discount code',
      discountData: null,
    };
  }

  // Check if code is active
  if (!discountConfig.isActive) {
    return {
      isValid: false,
      message: 'This discount code is no longer active',
      discountData: null,
    };
  }

  // Check date validity
  const now = new Date();
  if (now < discountConfig.validFrom) {
    return {
      isValid: false,
      message: 'This discount code is not yet valid',
      discountData: null,
    };
  }

  if (now > discountConfig.validUntil) {
    return {
      isValid: false,
      message: 'This discount code has expired',
      discountData: null,
    };
  }

  // Check usage limits
  if (discountConfig.maxUses && discountConfig.currentUses >= discountConfig.maxUses) {
    return {
      isValid: false,
      message: 'This discount code has reached its usage limit',
      discountData: null,
    };
  }
  // Check tenant validity if restricted
  if (discountConfig.allowedTenants && !discountConfig.allowedTenants.includes(currentTenant)) {
    return {
      isValid: false,
      message: 'This discount code is not valid for this brand',
      discountData: null,
    };
  }

  // Calculate discount amounts
  let discountAmount = Math.round((BASE_SUBSCRIPTION_PRICE * discountConfig.discountPercentage) / 100);
  let finalAmount = BASE_SUBSCRIPTION_PRICE - discountAmount;

  // Allow per-code final price override (used for redwoodadmin)
  if (discountConfig.finalPriceOverrideCents != null) {
    finalAmount = discountConfig.finalPriceOverrideCents; // final price in cents
    discountAmount = BASE_SUBSCRIPTION_PRICE - finalAmount;
  }

  return {
    isValid: true,
    message: `${discountConfig.description} applied!`,
    discountData: {
      code: normalizedCode,
      discountPercentage: discountConfig.discountPercentage,
      discountAmount,
      originalAmount: BASE_SUBSCRIPTION_PRICE,
      finalAmount,
      description: discountConfig.description,
    },
  };
}

/**
 * Calculates the final price after applying a discount
 * @param {string} code - The discount code
 * @param {string} currentTenant - Optional current tenant to validate against
 * @returns {Object} - Price calculation result
 */
export function calculateDiscountedPrice(code, currentTenant) {
  const validation = validateDiscountCode(code, currentTenant);

  if (!validation.isValid) {
    return {
      originalPrice: BASE_SUBSCRIPTION_PRICE,
      discountAmount: 0,
      finalPrice: BASE_SUBSCRIPTION_PRICE,
      discountPercentage: 0,
      isValid: false,
    };
  }

  return {
    originalPrice: BASE_SUBSCRIPTION_PRICE,
    discountAmount: validation.discountData.discountAmount,
    finalPrice: validation.discountData.finalAmount,
    discountPercentage: validation.discountData.discountPercentage,
    isValid: true,
  };
}

/**
 * Formats price from cents to dollars for display
 * @param {number} priceInCents - Price in cents
 * @returns {string} - Formatted price string
 */
export function formatPriceForDisplay(priceInCents) {
  const dollars = priceInCents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Gets all active discount codes
 * @returns {Array} - Array of active discount codes
 */
export function getActiveDiscountCodes() {
  const now = new Date();
  return Object.values(DISCOUNT_CODES).filter(code =>
    code.isActive &&
    now >= code.validFrom &&
    now <= code.validUntil
  );
}

/**
 * Checks if a discount code exists (case-insensitive)
 * @param {string} code - The discount code to check
 * @returns {boolean} - Whether the code exists
 */
export function discountCodeExists(code) {
  if (!code || typeof code !== 'string') return false;
  return Object.keys(DISCOUNT_CODES).includes(code.trim().toUpperCase());
}
